// ABOUTME: Listens for the completion of every job launched by the current user and announces it
// ABOUTME: with a translated snackbar in every tab of the launching browser, downloading the file
// ABOUTME: of an export once.

import { useJobTypeLabel } from '@src/_new/modules/job-list/registry';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useAuth } from '@src/hooks/auth/useAuth';
import { useJobFinishedSubscription } from '@src/modules/hierarchy/hierarchyQuery.generated';
import { downloadFile } from '@src/utils/hierarchies/downloadFile';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';

import {
    bystanderClaimDelayMs,
    claimJobDownload,
    markJobLaunchedInBrowser,
    wasJobLaunchedInBrowser,
} from './jobDownloadClaim';
import JobSubscriptionContext from './JobSubscriptionContext';

type JobOutcome = 'success' | 'error';

// Generic completion messages, built on the translated job type label.
const outcomeMessages = defineMessages({
    success: { id: 'job.finished.success', defaultMessage: '{job} completed' },
    error: { id: 'job.finished.error', defaultMessage: '{job} finished with errors' },
});

// Job types whose completion deserves a dedicated sentence instead of the generic one.
const JOB_OUTCOME_OVERRIDES: Record<string, Record<JobOutcome, MessageDescriptor>> = {
    'publish-family': defineMessages({
        success: {
            id: 'job.finished.publish-family.success',
            defaultMessage: 'Price list publish completed',
        },
        error: {
            id: 'job.finished.publish-family.error',
            defaultMessage: 'Price list publish failed',
        },
    }),
    'propagate-node-permissions': defineMessages({
        success: {
            id: 'job.finished.propagate-node-permissions.success',
            defaultMessage: 'The permissions have been applied to the child nodes',
        },
        error: {
            id: 'job.finished.propagate-node-permissions.error',
            defaultMessage:
                'The permissions were not applied to every child node: see the job list for details',
        },
    }),
};

type JobFile = { filename: string; type: string };

const JobSubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
    const intl = useIntl();
    const getJobTypeLabel = useJobTypeLabel();
    const { sessionData } = useAuth();
    const company = sessionData?.attributes?.company;
    const username = sessionData?.username;

    // activeJobIds: jobs launched from this tab that are still pending
    const [activeJobIds, setActiveJobIds] = useState<Set<string>>(new Set());
    // finishedJobIds: jobs completed; consumers read this to detect their job finishing
    const [finishedJobIds, setFinishedJobIds] = useState<Set<string>>(new Set());

    const addJobId = useCallback((id: string) => {
        markJobLaunchedInBrowser(id);
        setActiveJobIds(prev => new Set([...prev, id]));
    }, []);

    const clearFinishedJob = useCallback((id: string) => {
        setFinishedJobIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    // Every tab listens for the whole session to the jobs its user launched, so a job started
    // from another tab of this browser, or from one closed since, is announced here as well.
    const { data } = useJobFinishedSubscription({
        shouldResubscribe: true,
        variables: { company, username },
        skip: !company || !username,
    });

    useEffect(() => {
        if (!data?.jobFinished) return;

        const { jobId, filename, type, state } = data.jobFinished;
        if (!jobId) return;

        // The subscription reaches every browser and device of the user: only the browser that
        // launched the job announces it.
        const launchedHere = activeJobIds.has(jobId);
        if (!launchedHere && !wasJobLaunchedInBrowser(jobId)) return;

        const outcome: JobOutcome = state === 'error' ? 'error' : 'success';
        const override = type ? JOB_OUTCOME_OVERRIDES[type]?.[outcome] : undefined;
        const descriptor = override ?? outcomeMessages[outcome];
        const message = intl.formatMessage(descriptor, { job: getJobTypeLabel(type ?? '') });

        // A file is only downloaded for a job that ended: a failed export has nothing to hand out.
        const file: JobFile | undefined =
            outcome === 'success' && filename && type ? { filename, type } : undefined;

        if (outcome === 'error') {
            SnackBarUtils.error(message, { persist: true });
        } else if (!file) {
            SnackBarUtils.success(message, { persist: true });
        } else if (launchedHere) {
            claimJobDownload(jobId);
            downloadFile(file.filename, file.type);
            SnackBarUtils.success(message, { persist: true });
        } else {
            // The launching tab, when still open, downloads at once and claims the job. Any
            // other tab of the same browser waits a moment: if nobody claimed it (the launching
            // tab is gone) exactly one of them downloads, the others only announce it.
            setTimeout(() => {
                if (claimJobDownload(jobId)) {
                    downloadFile(file.filename, file.type);
                }
                SnackBarUtils.success(message, { persist: true });
            }, bystanderClaimDelayMs());
        }

        // Only a job launched from this tab has consumers waiting on it
        if (!launchedHere) return;

        // Move the job from active to finished so consumers can react
        setActiveJobIds(prev => {
            const next = new Set(prev);
            next.delete(jobId);
            return next;
        });
        setFinishedJobIds(prev => new Set([...prev, jobId]));
    }, [data]);

    const contextValue = useMemo(
        () => ({ addJobId, finishedJobIds, clearFinishedJob }),
        [addJobId, finishedJobIds, clearFinishedJob],
    );

    return (
        <JobSubscriptionContext.Provider value={contextValue}>
            {children}
        </JobSubscriptionContext.Provider>
    );
};

export default JobSubscriptionContextProvider;
